import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, CheckCircle, XCircle } from 'lucide-react';

function calcStrength(pw: string): 0 | 1 | 2 | 3 | 4 {
  if (!pw) return 0;
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  return score as 0 | 1 | 2 | 3 | 4;
}

const STRENGTH_COLORS = ['', '#ef4444', '#f59e0b', '#10b981', '#10b981'];
const STRENGTH_LABELS = ['', 'Weak', 'Fair', 'Strong', 'Very Strong'];

function generatePassword(): string {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789@#$!%*?&';
  return Array.from(crypto.getRandomValues(new Uint8Array(16)))
    .map(b => chars[b % chars.length]).join('');
}

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const strength = calcStrength(password);
  const reqs = [
    { label: 'At least 8 characters', met: password.length >= 8 },
    { label: 'One uppercase letter', met: /[A-Z]/.test(password) },
    { label: 'One number', met: /[0-9]/.test(password) },
    { label: 'One special character', met: /[^A-Za-z0-9]/.test(password) },
  ];

  useEffect(() => {
    if (done) {
      const t = setTimeout(() => navigate('/login'), 2000);
      return () => clearTimeout(t);
    }
  }, [done, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const pwRegex = /^(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9]).{8,}$/;
    if (!pwRegex.test(password)) { setError('Password does not meet requirements'); return; }
    if (password !== confirm) { setError('Passwords do not match'); return; }
    if (!token) { setError('Invalid or missing reset token'); return; }

    setLoading(true);
    try {
      const res = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword: password }),
      });
      const data = await res.json();
      if (data.success) {
        setDone(true);
      } else {
        setError(data.error ?? 'Failed to reset password');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = () => {
    const pw = generatePassword();
    setPassword(pw);
    setConfirm(pw);
    setShowPw(true);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0d1526',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20,
    }}>
      <div style={{
        width: '100%',
        maxWidth: 400,
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-default)',
        borderRadius: 'var(--radius-lg)',
        padding: '36px 32px',
        boxShadow: 'var(--shadow-lg)',
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <h1 style={{ margin: '0 0 4px', fontSize: '1.75rem', fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.5px' }}>
            <span>Pro</span><span style={{ color: 'var(--accent-primary)' }}>Q</span><span>ure</span>
          </h1>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>Reset your password</p>
        </div>

        {done ? (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <CheckCircle size={40} color="var(--accent-success)" style={{ margin: '0 auto 16px', display: 'block' }} />
            <p style={{ color: 'var(--text-primary)', fontSize: '0.9375rem', marginBottom: 6 }}>
              Password reset successfully.
            </p>
            <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Redirecting to login...</p>
          </div>
        ) : (
          <>
            {!token && (
              <div style={{
                background: 'rgba(239,68,68,0.1)',
                border: '1px solid rgba(239,68,68,0.3)',
                borderRadius: 8,
                padding: '10px 14px',
                marginBottom: 16,
                fontSize: 13,
                color: 'var(--accent-danger)',
              }}>
                Invalid or missing reset token. Please request a new password reset.
              </div>
            )}

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* New Password */}
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">New Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    className="form-input"
                    type={showPw ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter new password"
                    autoComplete="new-password"
                    style={{ width: '100%', paddingRight: 40, boxSizing: 'border-box' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((s) => !s)}
                    style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4, display: 'flex', alignItems: 'center' }}
                  >
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>

                {/* Strength bar */}
                {password && (
                  <div style={{ marginTop: 6 }}>
                    <div style={{ display: 'flex', gap: 3, marginBottom: 3 }}>
                      {[1, 2, 3, 4].map((n) => (
                        <div
                          key={n}
                          style={{
                            flex: 1, height: 3, borderRadius: 2,
                            background: n <= strength ? STRENGTH_COLORS[strength] : 'var(--border-default)',
                            transition: 'background 0.2s',
                          }}
                        />
                      ))}
                    </div>
                    <span style={{ fontSize: 10, color: STRENGTH_COLORS[strength] }}>{STRENGTH_LABELS[strength]}</span>
                  </div>
                )}
              </div>

              {/* Requirements checklist */}
              {password && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: -4 }}>
                  {reqs.map((r) => (
                    <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
                      {r.met
                        ? <CheckCircle size={11} color="var(--accent-success)" />
                        : <XCircle size={11} color="var(--text-muted)" />
                      }
                      <span style={{ color: r.met ? 'var(--accent-success)' : 'var(--text-muted)' }}>{r.label}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Confirm Password */}
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Confirm Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    className="form-input"
                    type={showConfirm ? 'text' : 'password'}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Repeat new password"
                    autoComplete="new-password"
                    style={{ width: '100%', paddingRight: 40, boxSizing: 'border-box' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm((s) => !s)}
                    style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4, display: 'flex', alignItems: 'center' }}
                  >
                    {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* Generate password */}
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={handleGenerate}
                style={{ alignSelf: 'flex-start' }}
              >
                Generate password
              </button>

              {error && (
                <div style={{ padding: '8px 12px', borderRadius: 'var(--radius-sm)', background: 'rgba(239,68,68,0.1)', color: 'var(--accent-danger)', fontSize: '0.875rem' }}>
                  {error}
                </div>
              )}

              <button
                className="btn btn-primary"
                type="submit"
                style={{ width: '100%' }}
                disabled={loading || !token}
              >
                {loading ? 'Saving…' : 'Reset Password'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
